import { Popconfirm } from 'antd';
import { Formik } from 'formik';
import { type PropsWithChildren, forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';

import type { MeasureUnitIntervalValue } from 'src/components/base/fields/SpecificationField/SpecificationIntervalField/SpecificationIntervalField.types';
import {
  type SpecificationIntervalPopupFormValues,
  specificationIntervalPopupFormValidationSchema,
} from 'src/components/base/fields/SpecificationField/SpecificationIntervalField/SpecificationIntervalPopup.validation';
import {
  SpecificationIntervalPopupFormContent,
  type SpecificationIntervalPopupFormContentRef,
} from 'src/components/base/fields/SpecificationField/SpecificationIntervalField/SpecificationIntervalPopupContent';
import { useModalExt } from 'src/hooks/useModalExt';
import type { SpecificationMeasureUnit } from 'src/types/reference/SpecificationMeasureUnit';

type SpecificationIntervalPopupFormProps = {
  multiselect: boolean;
  defaultMeasureUnit: SpecificationMeasureUnit | null;
  allowedMeasureUnits: SpecificationMeasureUnit[];
  minIntValue: number | null;
  maxIntValue: number | null;

  onSubmit: (values: SpecificationIntervalPopupFormValues) => Promise<void> | void;
};

const SpecificationIntervalPopupForm = forwardRef<
  SpecificationIntervalPopupFormContentRef,
  SpecificationIntervalPopupFormProps
>(
  (
    {
      multiselect,
      defaultMeasureUnit,
      allowedMeasureUnits,
      minIntValue,
      maxIntValue,
      onSubmit,
    }: Readonly<SpecificationIntervalPopupFormProps>,
    ref
  ) => {
    const specificationIntervalPopupFormContent = useRef<SpecificationIntervalPopupFormContentRef>(null);

    useImperativeHandle<SpecificationIntervalPopupFormContentRef, SpecificationIntervalPopupFormContentRef>(
      ref,
      () => ({
        submit: specificationIntervalPopupFormContent.current?.submit,
        isDisabled: specificationIntervalPopupFormContent.current?.isDisabled,
      }),
      []
    );

    return (
      <Formik<SpecificationIntervalPopupFormValues>
        initialValues={{
          values: [
            {
              minValue: null,
              maxValue: null,

              measureUnit: defaultMeasureUnit,
            },
          ],
        }}
        validationSchema={specificationIntervalPopupFormValidationSchema(minIntValue, maxIntValue)}
        onSubmit={onSubmit}
      >
        {(formikProps) => (
          <SpecificationIntervalPopupFormContent
            ref={specificationIntervalPopupFormContent}
            formikProps={formikProps}
            multiselect={multiselect}
            defaultMeasureUnit={defaultMeasureUnit}
            allowedMeasureUnits={allowedMeasureUnits}
          />
        )}
      </Formik>
    );
  }
);

type SpecificationIntervalPopupProps = PropsWithChildren<{
  multiselect: boolean;
  defaultMeasureUnit: SpecificationMeasureUnit | null;
  allowedMeasureUnits: SpecificationMeasureUnit[];
  minIntValue: number | null;
  maxIntValue: number | null;

  onClose?: () => void;
  onAddValue: (newCustomValues: MeasureUnitIntervalValue[]) => void;
}>;

export type SpecificationIntervalPopupRef = {
  open: () => void;
  close: () => void;
};

export const SpecificationIntervalPopup = forwardRef<SpecificationIntervalPopupRef, SpecificationIntervalPopupProps>(
  (
    { multiselect, defaultMeasureUnit, allowedMeasureUnits, minIntValue, maxIntValue, onAddValue, onClose, children },
    ref
  ) => {
    const specificationIntervalPopupForm = useRef<SpecificationIntervalPopupFormContentRef>(null);
    const { modalProps, onOpen, onClose: onModalClose } = useModalExt();

    const handleClose = useCallback(() => {
      onModalClose();

      onClose?.();
    }, [onModalClose, onClose]);

    const handleSuccess = useCallback(
      (values: SpecificationIntervalPopupFormValues) => {
        onAddValue(values.values as MeasureUnitIntervalValue[]);

        handleClose();
      },
      [onAddValue, handleClose]
    );

    const handleConfirm = useCallback(() => specificationIntervalPopupForm.current?.submit?.(), []);

    useImperativeHandle<SpecificationIntervalPopupRef, SpecificationIntervalPopupRef>(
      ref,
      () => ({
        open: () => {
          onOpen();
        },
        close: () => {
          handleClose();
        },
      }),
      [onOpen, handleClose]
    );

    useEffect(() => {
      if (modalProps.open) {
        const clickHandler = (event: MouseEvent) => {
          const popupElement = document.getElementsByClassName('specification-interval-popup')?.[0];

          if (
            popupElement &&
            // When press Remove item button on last element it becomes removed when this click handler fires and `popupElement.contains` is false
            document.contains(event.target as Element) &&
            (!(event.target instanceof Element) || !popupElement.contains(event.target))
          ) {
            window.removeEventListener('click', clickHandler);

            handleClose();
          }
        };

        // Even from click on Custom button is captured here. Need some time to avoid it
        setTimeout(() => window.addEventListener('click', clickHandler));

        return () => {
          window.removeEventListener('click', clickHandler);
        };
      }
    }, [modalProps.open, handleClose]);

    return (
      <Popconfirm
        placement="top"
        overlayClassName="specification-interval-popup"
        open={modalProps.open}
        destroyTooltipOnHide={true}
        title="Add Custom Value"
        description={
          <SpecificationIntervalPopupForm
            ref={specificationIntervalPopupForm}
            multiselect={multiselect}
            defaultMeasureUnit={defaultMeasureUnit}
            allowedMeasureUnits={allowedMeasureUnits}
            minIntValue={minIntValue}
            maxIntValue={maxIntValue}
            onSubmit={handleSuccess}
          />
        }
        okText="Add"
        cancelText="Cancel"
        icon={null}
        onConfirm={handleConfirm}
        onCancel={handleClose}
        okButtonProps={
          specificationIntervalPopupForm.current?.isDisabled != null
            ? {
                disabled: specificationIntervalPopupForm.current.isDisabled,
              }
            : undefined
        }
      >
        {children}
      </Popconfirm>
    );
  }
);
